// Phase 6.0 STEP D · 관리자 HOME 화면 관리센터 (통합 CMS)
// 5개 탭: HERO / 숏클립 / 공동구매 / 기획전·테마 / 뜨는 판매자
// 기존 Phase 5.9 및 STEP A/B/C 기능 무손상 재사용.
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Image as ImageIcon, Film, Users, Gift, Store,
  Plus, X, Trash2, Star, Eye, EyeOff, ArrowUp, ArrowDown, Pin, PinOff, Zap,
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const TABS = [
  { key: 'hero',    label: 'HERO',           icon: ImageIcon },
  { key: 'clips',   label: '숏클립',          icon: Film },
  { key: 'gb',      label: '공동구매',        icon: Users },
  { key: 'themes',  label: '기획전·테마',      icon: Gift },
  { key: 'sellers', label: '뜨는 판매자',      icon: Store },
];

// ─────────────────────────────────────────── HERO 탭
function HeroTab() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // null | 'new' | slide
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/home/hero-slides');
      setItems(r.data?.items || []);
    } catch (e) { toast.error('HERO 목록 로드 실패'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.hero_slide_id) {
        await api.patch(`/admin/home/hero-slides/${form.hero_slide_id}`, form);
        toast.success('HERO 수정');
      } else {
        await api.post('/admin/home/hero-slides', form);
        toast.success('HERO 등록');
      }
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
  };
  const del = async (sid) => {
    if (!window.confirm('이 HERO 슬라이드를 삭제하시겠습니까?')) return;
    try { await api.delete(`/admin/home/hero-slides/${sid}`); toast.success('삭제됨'); load(); }
    catch (e) { toast.error('삭제 실패'); }
  };
  const toggleActive = async (s) => {
    await api.patch(`/admin/home/hero-slides/${s.hero_slide_id}`, { is_active: !s.is_active });
    load();
  };
  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await api.post('/admin/home/hero-slides/reorder', { order: next.map((s) => s.hero_slide_id) });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-home-hero-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black">HERO Slider · {items.length}개</h2>
        <button onClick={() => setModal('new')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="hero-new-btn">
          <Plus size={13}/> HERO 추가
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-10">등록된 HERO가 없습니다.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s, i) => (
            <div key={s.hero_slide_id} className="rounded-2xl border border-black/10 overflow-hidden bg-white dark:bg-[#141414]" data-testid={`hero-row-${s.hero_slide_id}`}>
              <div className="relative aspect-[16/6] bg-gray-100">
                {s.image_url_pc && <img src={s.image_url_pc} alt="" className="absolute inset-0 w-full h-full object-cover"/>}
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"/>
                <div className="absolute inset-0 p-3 flex flex-col justify-end text-white">
                  <p className="text-sm font-black">{s.title || '(제목 없음)'}</p>
                  <p className="text-[10px] opacity-80 truncate">{s.subtitle}</p>
                </div>
                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-black ${s.is_active ? 'bg-emerald-500 text-white' : 'bg-gray-400 text-white'}`}>
                  {s.is_active ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="p-2 flex items-center gap-1 flex-wrap text-[11px]">
                <span className="text-gray-500 mr-auto">순서 #{s.sort_order}</span>
                <button onClick={() => move(i, -1)} className="p-1.5 rounded border border-black/10" data-testid={`hero-up-${s.hero_slide_id}`}><ArrowUp size={12}/></button>
                <button onClick={() => move(i, 1)} className="p-1.5 rounded border border-black/10" data-testid={`hero-down-${s.hero_slide_id}`}><ArrowDown size={12}/></button>
                <button onClick={() => toggleActive(s)} className={`px-2 py-1 rounded-full font-bold ${s.is_active ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`hero-toggle-${s.hero_slide_id}`}>
                  {s.is_active ? <Eye size={11} className="inline"/> : <EyeOff size={11} className="inline"/>} {s.is_active ? 'ON' : 'OFF'}
                </button>
                <button onClick={() => setModal(s)} className="px-2 py-1 rounded-full font-bold border border-black/10" data-testid={`hero-edit-${s.hero_slide_id}`}>수정</button>
                <button onClick={() => del(s.hero_slide_id)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`hero-del-${s.hero_slide_id}`}><Trash2 size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <HeroModal editing={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={save}/>}
    </div>
  );
}

function HeroModal({ editing, onClose, onSave }) {
  const [f, setF] = useState(editing || {
    title: '', subtitle: '', cta_label: '지금 참여하기', cta_link: '/schedule',
    image_url_pc: '', image_url_mobile: '', is_active: true,
    start_at: null, end_at: null,
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#141414] p-5 space-y-3" data-testid="hero-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black">{editing ? 'HERO 수정' : 'HERO 추가'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-black/5 rounded"><X size={16}/></button>
        </div>
        <input placeholder="제목" value={f.title || ''} onChange={(e) => set('title', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm" data-testid="hero-input-title"/>
        <input placeholder="서브문구" value={f.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="버튼명 (CTA)" value={f.cta_label || ''} onChange={(e) => set('cta_label', e.target.value)} className="px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
          <input placeholder="연결 URL (/schedule, /product/xxx)" value={f.cta_link || ''} onChange={(e) => set('cta_link', e.target.value)} className="px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        </div>
        <input placeholder="PC 이미지 URL" value={f.image_url_pc || ''} onChange={(e) => set('image_url_pc', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm" data-testid="hero-input-image-pc"/>
        <input placeholder="Mobile 이미지 URL (선택 · 미입력 시 PC 사용)" value={f.image_url_mobile || ''} onChange={(e) => set('image_url_mobile', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="text-[10px] font-bold text-gray-500">노출 시작</span>
            <input type="datetime-local" value={f.start_at ? String(f.start_at).slice(0, 16) : ''} onChange={(e) => set('start_at', e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full px-2 py-1.5 rounded border border-black/10 bg-transparent text-xs"/></label>
          <label className="block"><span className="text-[10px] font-bold text-gray-500">노출 종료</span>
            <input type="datetime-local" value={f.end_at ? String(f.end_at).slice(0, 16) : ''} onChange={(e) => set('end_at', e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full px-2 py-1.5 rounded border border-black/10 bg-transparent text-xs"/></label>
        </div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f.is_active} onChange={(e) => set('is_active', e.target.checked)}/> 즉시 노출 ON</label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-black/10 text-xs font-bold">취소</button>
          <button onClick={() => onSave(f)} className="px-4 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="hero-modal-save">저장</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── 숏클립 탭
function ClipsTab() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('approved');
  const load = useCallback(async () => {
    const r = await api.get(`/admin/short-clips?status=${tab}`);
    setItems(r.data?.items || []);
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  const act = async (cid, action, extra = {}) => {
    try { await api.post(`/admin/short-clips/${cid}/${action}`, extra); toast.success('반영됨'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };
  const toggleHidden = async (c) => {
    await api.patch(`/admin/short-clips/${c.clip_id}/visibility`, { hidden: !c.is_hidden });
    load();
  };
  const move = async (idx, dir) => {
    const next = [...items]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await api.post('/admin/short-clips/reorder', { order: next.map((c) => c.clip_id) });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-home-clips-tab">
      <div className="inline-flex rounded-full border border-black/10 overflow-hidden">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button key={s} onClick={() => setTab(s)} className={`px-3 py-1.5 text-xs font-bold ${tab === s ? 'bg-[#FF3B30] text-white' : ''}`} data-testid={`clips-tab-${s}`}>
            {({ pending: '심사 대기', approved: '승인·추천', rejected: '반려' })[s]}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-10">해당 상태의 클립이 없습니다.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((c, i) => (
            <div key={c.clip_id} className={`rounded-2xl border overflow-hidden bg-white dark:bg-[#141414] ${c.is_hidden ? 'opacity-50' : ''}`} data-testid={`clip-row-${c.clip_id}`}>
              <div className="relative aspect-[9/16] bg-gray-100">
                {c.thumbnail_url && <img src={c.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
                <div className="absolute top-1.5 left-1.5 flex gap-1 flex-wrap">
                  {c.is_featured && <span className="px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">★ 추천</span>}
                  {c.is_sponsored && <span className="px-1.5 py-0.5 rounded-full bg-black text-white text-[9px] font-black">Sponsored</span>}
                  {c.is_hidden && <span className="px-1.5 py-0.5 rounded-full bg-gray-700 text-white text-[9px] font-black">숨김</span>}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-2 text-white">
                  <p className="text-[10px] truncate">{c.seller_snapshot?.name}</p>
                  <p className="text-[11px] font-black truncate">{c.product_snapshot?.name}</p>
                </div>
              </div>
              <div className="p-2 flex flex-wrap gap-1 text-[10px]">
                {tab === 'pending' && (
                  <>
                    <button onClick={() => act(c.clip_id, 'approve')} className="px-2 py-1 rounded-full bg-emerald-500 text-white font-bold" data-testid={`clip-approve-${c.clip_id}`}>승인</button>
                    <button onClick={() => { const r = window.prompt('반려 사유:') || '부적합'; act(c.clip_id, 'reject', { reason: r }); }} className="px-2 py-1 rounded-full bg-red-500 text-white font-bold">반려</button>
                  </>
                )}
                {tab === 'approved' && (
                  <>
                    <button onClick={() => act(c.clip_id, 'feature', { featured: !c.is_featured })} className={`px-2 py-1 rounded-full font-bold ${c.is_featured ? 'bg-orange-500 text-white' : 'border border-orange-500/40 text-orange-600'}`} data-testid={`clip-feature-${c.clip_id}`}>
                      <Star size={10} className="inline"/> {c.is_featured ? '추천 해제' : '추천'}
                    </button>
                    <button onClick={() => act(c.clip_id, 'sponsor', { sponsored: !c.is_sponsored })} className={`px-2 py-1 rounded-full font-bold ${c.is_sponsored ? 'bg-black text-white' : 'border border-black/30 text-black dark:text-white'}`} data-testid={`clip-sponsor-${c.clip_id}`}>
                      <Zap size={10} className="inline"/> {c.is_sponsored ? '해제' : 'Sponsored'}
                    </button>
                    <button onClick={() => toggleHidden(c)} className="px-2 py-1 rounded-full font-bold border border-black/10" data-testid={`clip-hide-${c.clip_id}`}>
                      {c.is_hidden ? <Eye size={10} className="inline"/> : <EyeOff size={10} className="inline"/>} {c.is_hidden ? '노출' : '숨김'}
                    </button>
                    <button onClick={() => move(i, -1)} className="p-1 rounded border border-black/10"><ArrowUp size={10}/></button>
                    <button onClick={() => move(i, 1)} className="p-1 rounded border border-black/10"><ArrowDown size={10}/></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── 공동구매 탭
function GroupBuysTab() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get('/admin/home/group-buys');
    setItems(r.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePin = async (gb) => {
    try {
      await api.post(`/admin/group-buys/${gb.group_buy_id}/${gb.is_home_pinned ? 'unpin' : 'pin'}`);
      toast.success(gb.is_home_pinned ? '고정 해제' : '추천 고정');
      load();
    } catch (e) { toast.error('실패'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-home-gb-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black">공동구매 · {items.length}건</h2>
        <p className="text-[11px] text-gray-500">고정한 공구는 HOME 인기 공동구매 상단에 우선 노출됩니다.</p>
      </div>
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-10">활성 공동구매가 없습니다.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((gb) => {
            const pct = gb.target > 0 ? Math.min(100, Math.round((gb.current_count / gb.target) * 100)) : 0;
            return (
              <div key={gb.group_buy_id} className={`rounded-2xl border overflow-hidden bg-white dark:bg-[#141414] flex ${gb.is_home_pinned ? 'border-[#FF3B30] border-2' : 'border-black/10'}`} data-testid={`gb-row-${gb.group_buy_id}`}>
                <div className="w-24 h-24 shrink-0 bg-gray-100">
                  {gb.product_image && <img src={gb.product_image} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <p className="text-[10px] text-gray-500 truncate">{gb.seller_name}</p>
                  <p className="text-sm font-bold truncate">{gb.product_name}</p>
                  <p className="text-xs font-black text-[#FF3B30] mt-0.5">{won(gb.base_price)}</p>
                  <div className="mt-1.5 h-1 rounded-full bg-black/5 overflow-hidden">
                    <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px]">
                    <span className="text-gray-500">{gb.current_count}/{gb.target}명 · {pct}%</span>
                    <button onClick={() => togglePin(gb)} className={`px-2 py-1 rounded-full font-bold ${gb.is_home_pinned ? 'bg-[#FF3B30] text-white' : 'border border-[#FF3B30]/30 text-[#FF3B30]'}`} data-testid={`gb-pin-${gb.group_buy_id}`}>
                      {gb.is_home_pinned ? <><PinOff size={10} className="inline"/> 고정 해제</> : <><Pin size={10} className="inline"/> 추천 고정</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── 기획전·테마 탭
function ThemesTab() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [productPickerFor, setProductPickerFor] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get('/admin/home/themes');
    setItems(r.data?.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.theme_id) { await api.patch(`/admin/home/themes/${form.theme_id}`, form); toast.success('기획전 수정'); }
      else { await api.post('/admin/home/themes', form); toast.success('기획전 등록'); }
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };
  const del = async (tid) => {
    if (!window.confirm('이 기획전을 삭제하시겠습니까?')) return;
    await api.delete(`/admin/home/themes/${tid}`);
    toast.success('삭제됨'); load();
  };
  const toggleActive = async (t) => {
    await api.patch(`/admin/home/themes/${t.theme_id}`, { is_active: !t.is_active });
    load();
  };
  const move = async (idx, dir) => {
    const next = [...items]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await api.post('/admin/home/themes/reorder', { order: next.map((t) => t.theme_id) });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-home-themes-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black">기획전 · 테마 · {items.length}개</h2>
        <button onClick={() => setModal({})} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="theme-new-btn">
          <Plus size={13}/> 기획전 추가
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-10">등록된 기획전이 없습니다. "기획전 추가"로 첫 편성을 시작하세요.</p>
      ) : (
        <div className="space-y-2">
          {items.map((t, i) => (
            <div key={t.theme_id} className="rounded-2xl border border-black/10 bg-white dark:bg-[#141414] p-3 flex items-center gap-3" data-testid={`theme-row-${t.theme_id}`}>
              <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                {t.image_url && <img src={t.image_url} alt="" className="w-full h-full object-cover"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-black truncate">{t.name}</p>
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${t.is_active ? 'bg-emerald-500 text-white' : 'bg-gray-400 text-white'}`}>
                    {t.is_active ? 'ON' : 'OFF'}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 truncate">{t.description}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">상품 {t.product_count}개 · 순서 #{t.sort_order}</p>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="p-1 rounded border border-black/10"><ArrowUp size={10}/></button>
                  <button onClick={() => move(i, 1)} className="p-1 rounded border border-black/10"><ArrowDown size={10}/></button>
                </div>
                <div className="flex gap-1 text-[10px]">
                  <button onClick={() => setProductPickerFor(t)} className="px-2 py-1 rounded-full font-bold border border-black/10" data-testid={`theme-products-${t.theme_id}`}>상품 편집</button>
                  <button onClick={() => toggleActive(t)} className={`px-2 py-1 rounded-full font-bold ${t.is_active ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{t.is_active ? 'ON' : 'OFF'}</button>
                  <button onClick={() => setModal(t)} className="px-2 py-1 rounded-full font-bold border border-black/10">수정</button>
                  <button onClick={() => del(t.theme_id)} className="p-1 rounded text-red-500 hover:bg-red-50"><Trash2 size={11}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <ThemeModal editing={modal.theme_id ? modal : null} onClose={() => setModal(null)} onSave={save}/>}
      {productPickerFor && <ThemeProductPicker theme={productPickerFor} onClose={() => { setProductPickerFor(null); load(); }}/>}
    </div>
  );
}

function ThemeModal({ editing, onClose, onSave }) {
  const [f, setF] = useState(editing || { name: '', description: '', image_url: '', cta_link: '', is_active: true, start_at: null, end_at: null });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white dark:bg-[#141414] p-5 space-y-3" data-testid="theme-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black">{editing ? '기획전 수정' : '기획전 추가'}</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <input placeholder="테마명 (예: 여름 뷰티 WEEK)" value={f.name || ''} onChange={(e) => set('name', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm" data-testid="theme-input-name"/>
        <textarea rows={2} placeholder="설명 (선택)" value={f.description || ''} onChange={(e) => set('description', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        <input placeholder="대표 이미지 URL (선택)" value={f.image_url || ''} onChange={(e) => set('image_url', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        <input placeholder="상세 링크 (선택 · /schedule 등)" value={f.cta_link || ''} onChange={(e) => set('cta_link', e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <label><span className="text-[10px] font-bold text-gray-500">노출 시작</span>
            <input type="datetime-local" value={f.start_at ? String(f.start_at).slice(0, 16) : ''} onChange={(e) => set('start_at', e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full px-2 py-1.5 rounded border border-black/10 bg-transparent text-xs"/></label>
          <label><span className="text-[10px] font-bold text-gray-500">노출 종료</span>
            <input type="datetime-local" value={f.end_at ? String(f.end_at).slice(0, 16) : ''} onChange={(e) => set('end_at', e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full px-2 py-1.5 rounded border border-black/10 bg-transparent text-xs"/></label>
        </div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f.is_active} onChange={(e) => set('is_active', e.target.checked)}/> 즉시 노출 ON</label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-black/10 text-xs font-bold">취소</button>
          <button onClick={() => onSave(f)} className="px-4 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="theme-modal-save">저장</button>
        </div>
      </div>
    </div>
  );
}

function ThemeProductPicker({ theme, onClose }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(theme.product_ids || []);
  useEffect(() => {
    api.get('/admin/products').then((r) => {
      const arr = r.data?.items || r.data || [];
      setProducts(Array.isArray(arr) ? arr : []);
    }).catch(() => setProducts([]));
  }, []);
  const toggle = (pid) => {
    setSelected((prev) => prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]);
  };
  const move = (idx, dir) => {
    const next = [...selected]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSelected(next);
  };
  const save = async () => {
    try { await api.post(`/admin/home/themes/${theme.theme_id}/products`, { product_ids: selected }); toast.success('상품 편집 저장'); onClose(); }
    catch (e) { toast.error('실패'); }
  };
  const filtered = products.filter((p) => !q || (p.name || '').toLowerCase().includes(q.toLowerCase()));
  const byId = Object.fromEntries(products.map((p) => [p.product_id, p]));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-[#141414] flex flex-col" data-testid="theme-picker">
        <div className="flex items-center justify-between p-4 border-b border-black/5">
          <h3 className="text-base font-black">"{theme.name}" 상품 편집</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-hidden grid grid-cols-2">
          {/* Left: 전체 상품 */}
          <div className="p-3 border-r border-black/5 overflow-y-auto">
            <input placeholder="상품 검색" value={q} onChange={(e) => setQ(e.target.value)} className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm mb-3" data-testid="theme-picker-search"/>
            <div className="space-y-1">
              {filtered.slice(0, 80).map((p) => {
                const active = selected.includes(p.product_id);
                return (
                  <button key={p.product_id} onClick={() => toggle(p.product_id)} className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${active ? 'bg-[#FF3B30]/10' : 'hover:bg-black/5'}`} data-testid={`theme-picker-p-${p.product_id}`}>
                    <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden shrink-0">
                      {p.image && <img src={p.image} alt="" className="w-full h-full object-cover"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{p.name}</p>
                      <p className="text-[10px] text-gray-500">{won(p.sale_price)}</p>
                    </div>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-[#FF3B30] text-white' : 'border border-black/10'}`}>{active ? '✓' : '+'}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Right: 선택된 상품 순서 */}
          <div className="p-3 overflow-y-auto">
            <p className="text-xs font-bold text-gray-500 mb-2">선택된 상품 ({selected.length}개)</p>
            {selected.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-8">왼쪽에서 상품을 선택하세요.</p>
            ) : (
              <div className="space-y-1">
                {selected.map((pid, i) => {
                  const p = byId[pid] || { name: pid, product_id: pid };
                  return (
                    <div key={pid} className="flex items-center gap-2 p-1.5 rounded border border-black/5" data-testid={`theme-selected-${pid}`}>
                      <span className="text-[10px] font-black w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold truncate">{p.name}</p>
                      </div>
                      <button onClick={() => move(i, -1)} className="p-0.5"><ArrowUp size={12}/></button>
                      <button onClick={() => move(i, 1)} className="p-0.5"><ArrowDown size={12}/></button>
                      <button onClick={() => toggle(pid)} className="p-0.5 text-red-500"><Trash2 size={11}/></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="p-3 border-t border-black/5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-black/10 text-xs font-bold">취소</button>
          <button onClick={save} className="px-4 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="theme-picker-save">저장</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── 뜨는 판매자 탭
function SellersTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const load = useCallback(async () => {
    const r = await api.get(`/admin/home/sellers${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    setItems(r.data?.items || []);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const togglePin = async (s) => {
    try {
      await api.post(`/admin/sellers/${s.seller_id}/${s.is_home_pinned ? 'unpin-home' : 'pin-home'}`);
      toast.success(s.is_home_pinned ? '고정 해제' : '추천 고정');
      load();
    } catch (e) { toast.error('실패'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-home-sellers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-black">판매자 · {items.length}명</h2>
        <input placeholder="판매자명 검색" value={q} onChange={(e) => setQ(e.target.value)} className="px-3 py-1.5 rounded-full border border-black/10 bg-transparent text-sm" data-testid="sellers-search"/>
      </div>
      <p className="text-[11px] text-gray-500">추천 고정된 판매자는 HOME "지금 뜨는 판매자" 상단에 우선 노출됩니다.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((s) => (
          <div key={s.seller_id} className={`rounded-2xl border p-3 bg-white dark:bg-[#141414] ${s.is_home_pinned ? 'border-[#FF3B30] border-2' : 'border-black/10'}`} data-testid={`seller-row-${s.seller_id}`}>
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden shrink-0">
                {s.profile_image && <img src={s.profile_image} alt="" className="w-full h-full object-cover"/>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{s.name}</p>
                <p className="text-[10px] text-gray-500">팔로워 {s.followers_count || 0}</p>
              </div>
            </div>
            <button onClick={() => togglePin(s)} className={`mt-2 w-full px-2 py-1.5 rounded-full text-[11px] font-bold ${s.is_home_pinned ? 'bg-[#FF3B30] text-white' : 'border border-[#FF3B30]/30 text-[#FF3B30]'}`} data-testid={`seller-pin-${s.seller_id}`}>
              {s.is_home_pinned ? <><PinOff size={11} className="inline"/> 고정 해제</> : <><Pin size={11} className="inline"/> 추천 고정</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── Main
export default function AdminHome({ user }) {
  const [tab, setTab] = useState('hero');
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get('/admin/home/summary').then((r) => setSummary(r.data)).catch(() => {}); }, [tab]);

  const badge = (t) => {
    if (!summary) return null;
    const map = {
      hero: summary.hero?.total,
      clips: summary.short_clip?.pending || 0,
      gb: summary.group_buy?.pinned || 0,
      themes: summary.theme?.total,
      sellers: summary.seller?.pinned || 0,
    };
    const n = map[t.key];
    if (!n) return null;
    return <span className="ml-1 px-1.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">{n}</span>;
  };

  return (
    <AdminLayout user={user} active="home-cms">
      <div className="max-w-6xl mx-auto space-y-5" data-testid="admin-home-cms">
        <div>
          <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>🏠 홈 화면 관리</h1>
          <p className="text-xs text-gray-500 mt-1">HOME에 노출되는 편성 콘텐츠를 관리합니다. 지금 LIVE · 오늘 방송 예정 · NEW SELLER 는 자동 노출로 유지됩니다.</p>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-black/5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`shrink-0 inline-flex items-center gap-1 px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
                  active ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500 hover:text-black dark:hover:text-white'
                }`}
                data-testid={`admin-home-tab-${t.key}`}>
                <Icon size={14}/> {t.label} {badge(t)}
              </button>
            );
          })}
        </div>

        <div>
          {tab === 'hero' && <HeroTab/>}
          {tab === 'clips' && <ClipsTab/>}
          {tab === 'gb' && <GroupBuysTab/>}
          {tab === 'themes' && <ThemesTab/>}
          {tab === 'sellers' && <SellersTab/>}
        </div>
      </div>
    </AdminLayout>
  );
}
